import { Injectable } from '@angular/core';

/**
 * AWS Bedrock AI Service
 * Direct integration with AWS Bedrock for AI model calls (OpenAI or Claude)
 * Runs in Electron renderer process, credentials managed via environment or Electron main process
 */

export interface BedrockConfig {
  region: string;
  accessKeyId?: string;
  secretAccessKey?: string;
  modelId: 'anthropic.claude-v2' | 'anthropic.claude-v2:1' | 'anthropic.claude-3-sonnet' | 'anthropic.claude-3-haiku' | 'amazon.titan-text-express-v1';
  maxTokens?: number;
  temperature?: number;
}

export interface BedrockRequest {
  prompt: string;
  maxTokens?: number;
  temperature?: number;
  stopSequences?: string[];
}

export interface BedrockResponse {
  success: boolean;
  content: string;
  error?: string;
  usage?: {
    inputTokens: number;
    outputTokens: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class AwsBedrockService {
  private config: BedrockConfig = {
    region: 'us-east-1',
    modelId: 'anthropic.claude-3-sonnet', // Claude 3 Sonnet as default
    maxTokens: 4096,
    temperature: 0.7
  };

  constructor() {
    // Load configuration from environment or window object
    this.loadConfig();
  }

  /**
   * Load AWS Bedrock configuration from environment or Electron
   */
  private loadConfig(): void {
    // Load configuration from Electron IPC (async)
    this.loadConfigFromElectron();
  }

  /**
   * Async load configuration from Electron main process
   */
  private async loadConfigFromElectron(): Promise<void> {
    try {
      if ((window as any).host?.getBedrockConfig) {
        const config = await (window as any).host.getBedrockConfig();
        if (config) {
          if (config.region) {
            this.config.region = config.region;
          }
          if (config.modelId) {
            this.config.modelId = config.modelId;
          }
        }
      }
    } catch (error) {
      console.warn('[AwsBedrockService] Could not load config from Electron:', error);
    }
  }

  /**
   * Set AWS Bedrock configuration
   */
  setConfig(config: Partial<BedrockConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Get current configuration
   */
  getConfig(): BedrockConfig {
    return { ...this.config };
  }

  /**
   * Call AWS Bedrock AI model via Electron IPC
   * This delegates to Electron main process which has AWS SDK
   */
  async invoke(request: BedrockRequest): Promise<BedrockResponse> {
    console.log('[AwsBedrockService] 🤖 Starting AI invocation...');
    console.log('[AwsBedrockService] 📝 Model:', this.config.modelId);
    console.log('[AwsBedrockService] 📝 Region:', this.config.region);
    console.log('[AwsBedrockService] 📝 Max Tokens:', request.maxTokens || this.config.maxTokens);
    console.log('[AwsBedrockService] 📝 Temperature:', request.temperature || this.config.temperature);
    console.log('[AwsBedrockService] 📝 Prompt length:', request.prompt.length, 'characters');
    console.log('[AwsBedrockService] 📝 Prompt preview:', request.prompt.substring(0, 200) + '...');
    
    try {
      // Check if Electron IPC is available
      if ((window as any).host?.invokeBedrockAI) {
        console.log('[AwsBedrockService] ✅ Electron IPC available, calling via main process...');
        
        const startTime = Date.now();
        const result = await (window as any).host.invokeBedrockAI({
          modelId: this.config.modelId,
          region: this.config.region,
          prompt: request.prompt,
          maxTokens: request.maxTokens || this.config.maxTokens,
          temperature: request.temperature || this.config.temperature,
          stopSequences: request.stopSequences || []
        });
        
        const duration = Date.now() - startTime;
        console.log('[AwsBedrockService] ⏱️ AI call completed in', duration, 'ms');
        console.log('[AwsBedrockService] ✅ Success:', result.success);
        
        if (result.success) {
          console.log('[AwsBedrockService] 📤 Response length:', result.content?.length || 0, 'characters');
          console.log('[AwsBedrockService] 📤 Response preview:', result.content?.substring(0, 200) + '...');
          if (result.usage) {
            console.log('[AwsBedrockService] 📊 Input tokens:', result.usage.inputTokens);
            console.log('[AwsBedrockService] 📊 Output tokens:', result.usage.outputTokens);
          }
        } else {
          console.error('[AwsBedrockService] ❌ AI Error:', result.error);
        }
        
        return result;
      }

      // Fallback: Call via HTTP if Electron IPC not available (development mode)
      console.log('[AwsBedrockService] ⚠️ Electron IPC not available, using HTTP fallback');
      return await this.invokeFallback(request);
    } catch (error: any) {
      console.error('[AwsBedrockService] ❌ Error calling Bedrock:', error);
      console.error('[AwsBedrockService] ❌ Error stack:', error.stack);
      return {
        success: false,
        content: '',
        error: error.message || 'Failed to call AWS Bedrock'
      };
    }
  }

  /**
   * Fallback HTTP call for development/testing
   */
  private async invokeFallback(request: BedrockRequest): Promise<BedrockResponse> {
    console.warn('[AwsBedrockService] Using HTTP fallback - Electron IPC not available');
    
    try {
      const response = await fetch('http://localhost:3000/api/ai-chat/bedrock-invoke', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          modelId: this.config.modelId,
          region: this.config.region,
          prompt: request.prompt,
          maxTokens: request.maxTokens || this.config.maxTokens,
          temperature: request.temperature || this.config.temperature,
          stopSequences: request.stopSequences || []
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return {
        success: true,
        content: data.content || data.data || '',
        usage: data.usage
      };
    } catch (error: any) {
      return {
        success: false,
        content: '',
        error: error.message || 'Fallback HTTP call failed'
      };
    }
  }

  /**
   * Generate test steps using Claude/OpenAI via Bedrock
   */
  async generateTestSteps(prompt: string): Promise<string> {
    const response = await this.invoke({
      prompt,
      maxTokens: 4096,
      temperature: 0.7,
      stopSequences: ['\n\n---', '\n\nEND']
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to generate test steps');
    }

    return response.content;
  }

  /**
   * Stream response from Bedrock (for future implementation)
   */
  async invokeStream(request: BedrockRequest): Promise<AsyncIterable<string>> {
    // TODO: Implement streaming for better UX
    throw new Error('Streaming not yet implemented');
  }

  /**
   * Validate AWS credentials and connection
   */
  async validateConnection(): Promise<boolean> {
    try {
      const testResponse = await this.invoke({
        prompt: 'Hello, respond with "OK"',
        maxTokens: 10,
        temperature: 0
      });
      return testResponse.success;
    } catch {
      return false;
    }
  }
}
